import {
  InsertionPosition,
  WidgetAutocheck,
  WidgetConfig,
  WidgetElementType,
} from "@redotech/redo-model/widget";
import { assertNever } from "@redotech/util/type";
import { StorefrontCoverageHtmlElement } from "../cart-app";
import { TAPCART_SELECTOR } from "../product-page";
import {
  DEFAULT_RIC_PLACEMENT_SELECTOR,
  DEFAULT_TOGGLE_SELECTOR,
} from "../site-config/selectors";
import { getCartShadowRoot } from "../utils";

type DocOrShadow = ShadowRoot | Document;

export function getReactElementFromPlacement({
  widgetConfig,
  autocheck,
}: {
  widgetConfig: WidgetConfig;
  autocheck: WidgetAutocheck;
}): Element[] {
  const shadowRoot = getCartShadowRoot(
    autocheck,
    widgetConfig.shadowRootSelector,
  );

  const docElements: DocOrShadow[] = [];

  if (shadowRoot) {
    docElements.push(shadowRoot);

    if (widgetConfig.placeInsideAndOutsideShadowRoot) {
      docElements.push(document);
    }
  } else {
    docElements.push(document);
  }

  const foundElements: Element[] = [];

  for (const ele of docElements) {
    foundElements.push(
      ...Array.from(
        ele.querySelectorAll(
          widgetConfig.placementSelector || DEFAULT_RIC_PLACEMENT_SELECTOR,
        ),
      ),
    );
  }

  return foundElements;
}

export function getPlacedRedoToggle({
  container,
  widgetConfig,
  anyRedoToggle,
}: {
  container: HTMLElement | Element | null;
  widgetConfig?: WidgetConfig;
  anyRedoToggle: boolean;
}): StorefrontCoverageHtmlElement | undefined {
  if (!container) {
    return undefined;
  }

  if (anyRedoToggle) {
    return container?.querySelector(
      StorefrontCoverageHtmlElement.TAG,
    ) as StorefrontCoverageHtmlElement;
  }

  const toggles = container.querySelectorAll(
    StorefrontCoverageHtmlElement.TAG,
  ) as NodeListOf<StorefrontCoverageHtmlElement> | undefined;

  if (toggles) {
    for (const toggleElement of toggles) {
      if (toggleElement.toggleClass === widgetConfig?.elementType) {
        return toggleElement;
      }
    }
  }

  return undefined;
}

export function getPlacedCustomElement(
  customElementTag: string,
  container: Element,
  placementType: InsertionPosition,
): Element | null {
  const checkDirectChildren = (parent: Element): Element | null => {
    for (const child of parent.children) {
      if (child.tagName.toLowerCase() === customElementTag.toLowerCase()) {
        return child;
      }
    }
    return null;
  };

  switch (placementType) {
    case InsertionPosition.BEFORE:
    case InsertionPosition.AFTER:
      return container.parentElement
        ? checkDirectChildren(container.parentElement)
        : null;

    case InsertionPosition.PREPEND:
    case InsertionPosition.APPEND:
      return checkDirectChildren(container);

    default:
      assertNever(placementType);
      return null;
  }
}

export function hasCustomElementPlaced(
  customElementTag: string,
  selector: string,
  placementType: InsertionPosition,
): boolean {
  const elements = document.querySelectorAll(selector);
  for (const element of elements) {
    if (getPlacedCustomElement(customElementTag, element, placementType)) {
      return true;
    }
  }
  return false;
}

export function widgetSortOrder(widget?: WidgetConfig): number {
  const widgetType: WidgetElementType | undefined = widget?.elementType;
  if (!widgetType) {
    return 0;
  }

  const sign = widget?.insertionPosition === InsertionPosition.APPEND ? -1 : 1;
  switch (widgetType) {
    case WidgetElementType.BOTH_CHECKOUT_BUTTONS:
      return 1 * sign;
    case WidgetElementType.INFO_CARD:
      return 2 * sign;
    case WidgetElementType.NON_COVERAGE_CHECKOUT_BUTTON:
      return 3 * sign;
    case WidgetElementType.COVERAGE_CHECKOUT_BUTTON:
      return 4 * sign;
    case WidgetElementType.SINGLE_TOGGLE:
      return 5 * sign;
    case WidgetElementType.DOUBLE_TOGGLE:
      return 6 * sign;
    case WidgetElementType.CART_CARD:
      return 7 * sign;
    case WidgetElementType.CHECKOUT_BUTTON:
      return 8 * sign;
    default:
      assertNever(widgetType);
      return 0;
  }
}

/**
 * WidgetMatchingSelectorTargets
 *
 * Before Redo's custom element have been created, but after having found all of the matching elements that Redo should be placed by.
 * WidgetConfigs will be included for all Checkout buttons, info cards, and for toggles on ANY_WIDGET_* strategies.
 */
export interface WidgetMatchingSelectorTargets {
  elements: Element[];
  widgetConfig?: WidgetConfig;
}

/**
 * PlacedWidgetHtmlElements
 *
 * After Redo's custom elements are placed for each widget type.
 * WidgetConfigs will be included for all Checkout buttons, info cards, and for toggles on ANY_WIDGET_* strategies.
 */
export interface PlacedWidgetHtmlElements {
  redoHtmlElements: StorefrontCoverageHtmlElement[];
  widgetConfig?: WidgetConfig;
}

export const getPlacementLocationsForStandardToggles = ({
  autocheck,
  onTapcart,
}: {
  autocheck: WidgetAutocheck;
  onTapcart: boolean;
}): WidgetMatchingSelectorTargets => {
  if (autocheck.cartToggle.cartToggleShadowRootSelector) {
    const shadowRoot = getCartShadowRoot(autocheck);
    let shadowRootLocations: Element[] = [];
    shadowRootLocations = shadowRoot
      ? Array.from(
          shadowRoot.querySelectorAll(
            onTapcart
              ? TAPCART_SELECTOR
              : autocheck.cartToggle.cartToggleShadowRootSelector ||
                  DEFAULT_TOGGLE_SELECTOR,
          ),
        )
      : [];

    return {
      elements: [
        ...Array.from(
          document.querySelectorAll(
            onTapcart
              ? TAPCART_SELECTOR
              : autocheck.cartToggle.cartToggleSelector ||
                  DEFAULT_TOGGLE_SELECTOR,
          ),
        ),
        ...shadowRootLocations,
      ],
    };
  } else {
    const docOrShadowRoot = getCartShadowRoot(autocheck) ?? document;
    return {
      elements: Array.from(
        docOrShadowRoot.querySelectorAll(
          onTapcart
            ? TAPCART_SELECTOR
            : autocheck.cartToggle.cartToggleSelector ||
                DEFAULT_TOGGLE_SELECTOR,
        ),
      ),
    };
  }
};
